import { useState } from 'react';
import { BookOpen, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useFeatureRegistration, type FeatureMeta } from '@/contexts/FeatureRegistryContext';
import { saveCardOutcome } from '@/lib/chatCards';
import { cn } from '@/lib/utils';
import type { MemoryForgetProposal } from './streamChat';

export const FEATURE_META: FeatureMeta = {
  id: 'chat-memory-forget',
  label: 'Remove something from memory in chat',
  description:
    'Ask Ask Cannafax to forget something and a card shows the exact entry, so you remove it yourself with one click.',
  triggerType: 'action',
  personas: ['seller', 'buyer_seller', 'buyer'],
};

/** Plain section names for a saved entry, matching the Memory panel's wording. */
const KIND_LABEL: Record<MemoryForgetProposal['entries'][number]['kind'], string> = {
  preference: 'House rule',
  commitment: 'Promise',
  deal_outcome: 'Past deal',
  chat_takeaway: 'Takeaway',
};

/**
 * The model only points at what the user wants gone; this card shows the exact
 * entries and only the user's click removes one (the same RLS-scoped update the
 * Memory panel uses). Nothing is removed by the assistant itself.
 */
export function ChatMemoryForgetCard({ proposal, threadId }: { proposal: MemoryForgetProposal; threadId?: string | null }) {
  useFeatureRegistration(FEATURE_META);
  const [removed, setRemoved] = useState<string[]>(proposal.outcome?.removedIds ?? []);
  const allGone = removed.length >= proposal.entries.length;

  const forget = async (id: string) => {
    const { error } = await supabase.from('desk_memories').update({ status: 'dismissed' }).eq('id', id);
    if (error) {
      toast.error('Could not remove that entry');
      return;
    }
    const next = [...removed, id];
    setRemoved(next);
    void saveCardOutcome(threadId, proposal.cardId, {
      state: next.length >= proposal.entries.length ? 'done' : 'partial',
      removedIds: next,
    });
    toast.success('Removed from your memory');
  };

  return (
    <div className="mt-2 rounded-lg border bg-card p-3 text-sm">
      <div className="flex items-start gap-2">
        <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <p className="min-w-0 flex-1 text-muted-foreground">
          {allGone ? 'Removed from your memory.' : 'Remove this from your memory? Only you can see it, and nothing goes until you press Remove.'}
        </p>
      </div>
      <ul className="mt-2 space-y-1.5">
        {proposal.entries.map((e) => {
          const gone = removed.includes(e.id);
          return (
            <li
              key={e.id}
              className={cn(
                'flex items-start gap-2 rounded-md border border-border px-3 py-2',
                gone && 'opacity-60',
              )}
            >
              <span className="min-w-0 flex-1">
                <span className="block text-xs text-muted-foreground">{KIND_LABEL[e.kind]}</span>
                <span className={cn('block', gone && 'line-through')}>{e.content}</span>
              </span>
              {!gone && (
                <Button
                  size="sm"
                  variant={proposal.entries.length === 1 ? 'default' : 'quiet'}
                  onClick={() => void forget(e.id)}
                >
                  Remove
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      {allGone && (
        <div className="mt-2 flex items-center gap-1.5 text-muted-foreground">
          <CheckCircle2 className="h-4 w-4" aria-hidden /> Done — your listings, menus and messages are untouched.
        </div>
      )}
    </div>
  );
}
